/**
 * app.js
 *
 * This is the entry file for the application, only setup and boilerplate
 * code.
 */

// Needed for redux-saga es6 generator support
import '@babel/polyfill';

// Import all the third party stuff
import React from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { ConnectedRouter } from 'react-router-redux';
import FontFaceObserver from 'fontfaceobserver';
import 'sanitize.css/sanitize.css';

import noty from 'noty';

// Import C3 / Chart specific CSS
import 'ff-vis-js/src/FFVis/style.scss';
import 'ff-vis-js/src/FFVis/stylesheets/c3.css';
import 'ff-vis-js/src/FFVis/stylesheets/FFC3.css';

import history from 'utils/history/historyObject';

// Import root app

import 'utils/sentry';
import App from 'containers/App';
import { GRAPHQL_URL } from 'containers/App/constants';

// Import Language Provider
import LanguageProvider from 'containers/LanguageProvider';

// Load the favicon, the manifest.json file and the .htaccess file
/* eslint-disable import/no-webpack-loader-syntax */
import '!file-loader?name=[name].[ext]!./images/favicon.ico';
import '!file-loader?name=[name].[ext]!./images/apple-icon-57x57.png';
import '!file-loader?name=[name].[ext]!./images/apple-icon-60x60.png';
import '!file-loader?name=[name].[ext]!./images/apple-icon-72x72.png';
import '!file-loader?name=[name].[ext]!./images/apple-icon-76x76.png';
import '!file-loader?name=[name].[ext]!./images/apple-icon-114x114.png';
import '!file-loader?name=[name].[ext]!./images/apple-icon-120x120.png';
import '!file-loader?name=[name].[ext]!./images/apple-icon-144x144.png';
import '!file-loader?name=[name].[ext]!./images/apple-icon-152x152.png';
import '!file-loader?name=[name].[ext]!./images/apple-icon-180x180.png';
import '!file-loader?name=[name].[ext]!./images/android-icon-192x192.png';
import '!file-loader?name=[name].[ext]!./images/favicon-32x32.png';
import '!file-loader?name=[name].[ext]!./images/favicon-96x96.png';
import '!file-loader?name=[name].[ext]!./images/favicon-16x16.png';
import '!file-loader?name=[name].[ext]!./manifest.json';
import 'file-loader?name=[name].[ext]!./.htaccess'; // eslint-disable-line import/extensions
/* eslint-enable import/no-webpack-loader-syntax */

import * as Sentry from '@sentry/react';

import configureStore from './configureStore';

// Import i18n messages
import { translationMessages } from './i18n';

// Import CSS reset and Global Styles
import './global-styles';

// Create redux store with history
const initialState = {};
export const store = configureStore(initialState, history);

// Required for codebook
window.GRAPHQL_URL = GRAPHQL_URL;

window.noty = noty;

// Observe loading of Open Sans (to remove open sans, remove the <link> tag in
// the index.html file and this observer)
const openSansObserver = new FontFaceObserver('Open Sans', {});

// When Open Sans is loaded, add a font-family using Open Sans to the body
openSansObserver.load().then(() => {
  document.body.classList.add('fontLoaded');
}, () => {
  document.body.classList.remove('fontLoaded');
});

const MOUNT_NODE = document.getElementById('app');

const render = (messages) => {
  ReactDOM.render(
    <Provider store={store}>
      <LanguageProvider messages={messages}>
        <ConnectedRouter history={history}>
          <Sentry.ErrorBoundary
            fallback={<p>An error has occurred</p>}
            onError={(error, errorInfo) => {
              console.log(error, errorInfo);
            }}
          >
            <App />
          </Sentry.ErrorBoundary>
        </ConnectedRouter>
      </LanguageProvider>
    </Provider>,
    MOUNT_NODE
  );
};

if (module.hot) {
  // Hot reloadable React components and translation json files
  // modules.hot.accept does not accept dynamic dependencies,
  // have to be constants at compile-time
  module.hot.accept(['./i18n', 'containers/App'], () => {
    ReactDOM.unmountComponentAtNode(MOUNT_NODE);
    render(translationMessages);
  });
}

// Chunked polyfill for browsers without Intl support
if (!window.Intl) {
  (new Promise((resolve) => {
    resolve(import('intl'));
  }))
    .then(() => Promise.all([
      import('intl/locale-data/jsonp/en.js'),
      import('intl/locale-data/jsonp/de.js'),
    ]))
    .then(() => render(translationMessages))
    .catch((err) => {
      throw err;
    });
} else {
  render(translationMessages);
}



// WEBPACK FOOTER //
// ./app/app.js